import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { ResponsiveDialog, useSelection, useSettings, useUserData, Utils } from 'app/main/components';
import { ViewTable } from 'app/main/components/page-layouts';
import { renderCellWithTooltip } from 'app/main/components/page-layouts/view/table/ViewTableUtils';

interface OrdersLinesAddStandardNotesDialogProps {
	openDialog: boolean;
	closeDialog: VoidFunction;
	orderUid: string;
	updateRefresh: VoidFunction;
}

export default function OrdersLinesAddStandardNotesDialog(props: OrdersLinesAddStandardNotesDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const userData = useUserData();

	const { selectedItems } = useSelection({ module: 'orders_standardnotes', isSelecting: true });

	function addLines() {
		dispatch(Utils.alertProgressMsg(t));
		Utils.executeStored({
			action: 'st_orders_lines_add',
			dispatch,
			data: {
				orli_ords_uid: props.orderUid,
				orli_user_created_uid: userData.user_uid,
				lines: [].concat(
					...selectedItems.map(item =>
						item.osnt_rows?.map(row => ({
							orli_type: 'note',
							orli_description: row.osnr_description
						}))
					)
				)
			},
			successHandler: () => {
				dispatch(Utils.alertSuccessMsg(t));
				props.closeDialog();
				props.updateRefresh();
			}
		});
	}

	const columns: GridColDef[] = [
		{
			field: 'osnt_title',
			headerName: t('DETAILS_ORDERS_STANDARDNOTES_FIELD_TITLE'),
			flex: 1,
			renderCell: renderCellWithTooltip,
			hideable: false
		}
	];

	return (
		<ResponsiveDialog open={props.openDialog} onClose={props.closeDialog} maxWidth="sm" fullWidth>
			<DialogTitle>{t('ORDERS_STANDARDNOTES_ADD')}</DialogTitle>
			<DialogContent className="pb-0">
				<ViewTable
					module="orders_standardnotes"
					columns={columns}
					showExportButton={false}
					selectionProps={{ mode: 'multiple', selectedItems }}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={props.closeDialog}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button color="secondary" onClick={addLines} disabled={selectedItems.length === 0}>
					{t('DIALOG_ACTION_ADD')}
				</Button>
			</DialogActions>
		</ResponsiveDialog>
	);
}
